import { storeToRefs } from 'pinia';
import { useCartStore } from '../stores/cart';
import { CartItemQuantity } from '../types/models';
import { AddToCartItem, ShopifyCart } from '../types/servicesShopifyCart';
import { useShopifyCartService } from './shopifyCart';
import { useCartValidation } from '../composables/useCartValidation';

export const cartActions = () => {
  const { DEFAULT_EMPTY_CART, updateCart, getCart, clearCart, addToCart } = useShopifyCartService();
  const cartStore = useCartStore();
  const { setCart, setCartUpdateInProgress } = cartStore;
  const { cartData } = storeToRefs(cartStore);
  const { validateCart } = useCartValidation();

  const refreshCart = async (): Promise<ShopifyCart> => {
    setCartUpdateInProgress(true);
    try {
      const cart = await getCart();
      const validatedCart = await validateCart(cart);
      setCart(validatedCart);
      return validatedCart;
    } catch (error) {
      console.error(error);
      const defaultEmptyCart = DEFAULT_EMPTY_CART();
      setCart(defaultEmptyCart);
      return defaultEmptyCart;
    }
  };

  const addCartItems = async (items: AddToCartItem[]): Promise<ShopifyCart> => {
    setCartUpdateInProgress(true);
    try {
      await addToCart(items);
      const cart = await getCart();
      const validatedCart = await validateCart(cart);
      setCart(validatedCart);
      return validatedCart;
    } catch (error) {
      console.error(error);
      setCartUpdateInProgress(false);
      return cartData.value;
    }
  };

  const updateCartItemQuantities = async (items: CartItemQuantity[]): Promise<ShopifyCart> => {
    setCartUpdateInProgress(true);
    try {
      const updates: Record<string, number> = items.reduce((updateObject, { key, quantity }) => {
        const currentItem = cartData.value.items.find((item) => key === item.key);
        if (!currentItem) {
          throw new Error(`Item with key ${key} doesn't exist in cart`);
        }
        updateObject[key] = quantity;
        return updateObject;
      }, {});
      const cart = await updateCart(updates);
      const validatedCart = await validateCart(cart);
      setCart(validatedCart);
      return validatedCart;
    } catch (error) {
      console.error(error);
      setCartUpdateInProgress(false);
      return cartData.value;
    }
  };

  const removeCartItems = async (items: string[]): Promise<ShopifyCart> => {
    const formattedItems: CartItemQuantity[] = items.map((key) => ({ key, quantity: 0 }));
    return updateCartItemQuantities(formattedItems);
  };

  const emptyCart = async (): Promise<ShopifyCart> => {
    setCartUpdateInProgress(true);
    const cart = await clearCart();
    setCart(cart);
    return cart;
  };

  return {
    refreshCart,
    addCartItems,
    updateCartItemQuantities,
    removeCartItems,
    emptyCart,
  };
};
